(() => {
  'use strict';

  if (window.CorsailleHomeV2?.initialize) {
    window.CorsailleHomeV2.initialize(document);
    return;
  }

  const HOME_SELECTOR = '[data-corsaille-home]';
  const instances = new WeakMap();
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let animationEnvironmentReady = false;

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const prefersReducedMotion = () => motionQuery.matches;
  const isControl = (target) => Boolean(target?.closest?.('button,input,select,textarea,[contenteditable="true"]'));

  const rootsWithin = (scope = document) => {
    const roots = [];
    if (scope?.nodeType === 1 && scope.matches?.(HOME_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(HOME_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const listenForMotionPreference = (listener, signal) => {
    if (motionQuery.addEventListener) {
      motionQuery.addEventListener('change', listener, { signal });
      return;
    }
    motionQuery.addListener(listener);
    signal.addEventListener('abort', () => motionQuery.removeListener(listener), { once: true });
  };

  const prepareAnimationEnvironment = () => {
    if (animationEnvironmentReady) return;
    animationEnvironmentReady = true;
    document.documentElement.classList.add('animations-ready');
    try {
      const firstLoad = !window.sessionStorage.getItem('corsaille-visited');
      document.documentElement.classList.toggle('is-first-load', firstLoad);
      window.sessionStorage.setItem('corsaille-visited', '1');
    } catch (_) {
      document.documentElement.classList.remove('is-first-load');
    }
  };

  class HeroController {
    constructor(hero, signal) {
      this.hero = hero;
      this.signal = signal;
      this.stage = hero.querySelector('.chero__stage');
      this.slides = [...hero.querySelectorAll('[data-hero-slide]')];
      this.selectors = [...hero.querySelectorAll('[data-hero-selector]')];
      this.cta = hero.querySelector('[data-hero-cta]');
      this.ctaLabel = hero.querySelector('[data-hero-cta-label]');
      this.config = this.readConfig(hero.querySelector('[data-hero-config]'));
      this.timer = 0;
      this.progressFrame = 0;
      this.ctaTimer = 0;
      this.ctaFrame = 0;
      this.gesture = null;
      this.suppressClickUntil = 0;
      this.leavingTimers = new Map();
      this.visibilityObserver = null;
      this.pointerInside = false;
      this.focusWithin = false;
      this.manualStopped = false;
      this.destroyed = false;
      this.ready = false;

      const requestedDuration = Number(hero.dataset.autoplaySeconds || 5.8) * 1000;
      this.duration = Number.isFinite(requestedDuration)
        ? clamp(requestedDuration, 4000, 10000)
        : 5800;
      const requestedCrossfade = Number(hero.dataset.crossfadeMs || 1120);
      this.crossfade = Number.isFinite(requestedCrossfade)
        ? clamp(requestedCrossfade, 600, 1800)
        : 1120;

      const requestedActive = Number.parseInt(hero.dataset.activeSlide, 10);
      const markedActive = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.active = Number.isInteger(requestedActive) && requestedActive >= 0 && requestedActive < this.slides.length
        ? requestedActive
        : Math.max(0, markedActive);

      const bounds = hero.getBoundingClientRect();
      this.inViewport = bounds.bottom > 0 && bounds.top < (window.innerHeight || document.documentElement.clientHeight || 1);
      hero.style.setProperty('--hero-duration', `${this.duration / 1000}s`);

      signal.addEventListener('abort', () => this.destroy(), { once: true });
      if (!this.stage || this.slides.length < 2 || !this.cta || !this.ctaLabel) return;
      this.ready = true;
      this.applyInitialState();
      this.bindSelectors();
      this.bindGestures();
      this.bindAutoplayConditions();
      this.prepareImage((this.active + 1) % this.slides.length);
      this.syncAutoplay();
    }

    readConfig(node) {
      try {
        const value = JSON.parse(node?.textContent || '[]');
        return Array.isArray(value) ? value : [];
      } catch (_) {
        return [];
      }
    }

    applyInitialState() {
      this.slides.forEach((slide, index) => {
        const selected = index === this.active;
        slide.classList.toggle('is-active', selected);
        slide.classList.remove('is-leaving');
        slide.setAttribute('aria-hidden', String(!selected));
      });
      this.selectors.forEach((selector, index) => {
        const selected = index === this.active;
        selector.classList.toggle('is-active', selected);
        selector.setAttribute('aria-selected', String(selected));
        selector.tabIndex = selected ? 0 : -1;
      });
      this.hero.dataset.activeSlide = String(this.active);
      this.updateCTA(this.active, true);
      this.prepareImage(this.active);
    }

    bindSelectors() {
      this.selectors.forEach((selector, index) => {
        selector.addEventListener('click', () => this.show(index, 'selector'), { signal: this.signal });
        selector.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.slides.length - 1;
          if (event.key === 'ArrowRight') next = (index + 1) % this.slides.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.slides.length) % this.slides.length;
          this.show(next, 'keyboard');
        }, { signal: this.signal });
      });
    }

    bindGestures() {
      this.stage.addEventListener('dragstart', (event) => event.preventDefault(), { signal: this.signal });

      if ('PointerEvent' in window) {
        this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
        this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
        this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal: this.signal });
        this.stage.addEventListener('pointercancel', (event) => this.clearGesture(event, true), { signal: this.signal });
        this.stage.addEventListener('lostpointercapture', (event) => this.clearGesture(event, true), { signal: this.signal });
        this.stage.addEventListener('pointerleave', (event) => {
          if (this.gesture && !this.gesture.captured) this.clearGesture(event, true);
        }, { signal: this.signal });
      } else {
        this.bindTouchFallback();
      }

      this.stage.addEventListener('click', (event) => {
        if (performance.now() >= this.suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true, signal: this.signal });
    }

    bindTouchFallback() {
      let touch = null;
      this.stage.addEventListener('touchstart', (event) => {
        const point = event.changedTouches[0];
        if (!point) return;
        this.stopAutoplay();
        touch = { id: point.identifier, x: point.clientX, y: point.clientY };
      }, { passive: true, signal: this.signal });
      this.stage.addEventListener('touchend', (event) => {
        if (!touch) return;
        const point = [...event.changedTouches].find((candidate) => candidate.identifier === touch.id);
        const current = touch;
        touch = null;
        if (!point) {
          this.syncAutoplay();
          return;
        }
        const dx = point.clientX - current.x;
        const dy = point.clientY - current.y;
        if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.suppressClickUntil = performance.now() + 420;
          this.showStep(dx < 0 ? 1 : -1, 'swipe');
        } else {
          this.syncAutoplay();
        }
      }, { passive: true, signal: this.signal });
      this.stage.addEventListener('touchcancel', () => {
        touch = null;
        this.syncAutoplay();
      }, { passive: true, signal: this.signal });
    }

    onPointerDown(event) {
      if (!event.isPrimary || event.button !== 0 || isControl(event.target)) return;
      this.stopAutoplay();
      this.gesture = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        startedAt: performance.now(),
        axis: null,
        moved: false,
        captured: false,
      };
    }

    onPointerMove(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      const absoluteX = Math.abs(dx);
      const absoluteY = Math.abs(dy);
      if (!gesture.axis && Math.max(absoluteX, absoluteY) > 8) {
        if (absoluteX > absoluteY * 1.18) gesture.axis = 'x';
        else if (absoluteY > absoluteX * 1.18) gesture.axis = 'y';
      }
      if (gesture.axis !== 'x') return;
      gesture.moved = true;
      if (!gesture.captured) {
        try {
          this.stage.setPointerCapture?.(event.pointerId);
          gesture.captured = Boolean(this.stage.hasPointerCapture?.(event.pointerId));
        } catch (_) {
          gesture.captured = false;
        }
        this.hero.classList.add('is-manual-dragging');
      }
      this.hero.style.setProperty('--manual-drag-x', `${clamp(dx * 0.16, -32, 32).toFixed(1)}px`);
      if (event.cancelable) event.preventDefault();
    }

    onPointerUp(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      const elapsed = Math.max(1, performance.now() - gesture.startedAt);
      const displacement = Math.abs(dx);
      const horizontal = gesture.axis === 'x' && displacement > Math.abs(dy) * 1.25;
      const committed = horizontal && displacement >= 18 && (displacement > 54 || displacement / elapsed > 0.55);
      if (committed || gesture.moved) this.suppressClickUntil = performance.now() + 420;
      this.clearGesture(event, false);
      if (committed) this.showStep(dx < 0 ? 1 : -1, 'drag');
      else this.syncAutoplay();
    }

    clearGesture(event, resume) {
      const gesture = this.gesture;
      if (!gesture) return;
      if (Number.isFinite(event?.pointerId) && event.pointerId !== gesture.id) return;
      this.gesture = null;
      try {
        if (this.stage.hasPointerCapture?.(gesture.id)) this.stage.releasePointerCapture(gesture.id);
      } catch (_) {
        // Pointer capture can already be released by a browser gesture.
      }
      this.hero.classList.remove('is-manual-dragging');
      this.hero.style.removeProperty('--manual-drag-x');
      if (resume) this.syncAutoplay();
    }

    bindAutoplayConditions() {
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: this.signal });
      this.hero.addEventListener('mouseenter', () => {
        this.pointerInside = true;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.hero.addEventListener('mouseleave', () => {
        this.pointerInside = false;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.hero.addEventListener('focusin', () => {
        this.focusWithin = true;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.hero.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.hero.contains(event.relatedTarget)) return;
        this.focusWithin = false;
        this.syncAutoplay();
      }, { signal: this.signal });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries.find((candidate) => candidate.target === this.hero);
          if (!entry) return;
          const nextState = entry.isIntersecting && entry.intersectionRatio > 0;
          if (nextState === this.inViewport) return;
          this.inViewport = nextState;
          this.syncAutoplay();
        }, { threshold: [0, 0.02] });
        this.visibilityObserver.observe(this.hero);
      }
    }

    canAutoplay() {
      return !this.destroyed
        && this.ready
        && !this.signal.aborted
        && !prefersReducedMotion()
        && !document.hidden
        && this.inViewport
        && !this.pointerInside
        && !this.focusWithin
        && !this.manualStopped
        && !this.gesture;
    }

    syncAutoplay() {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;
      this.progressFrame = window.requestAnimationFrame(() => {
        this.progressFrame = 0;
        if (this.canAutoplay()) this.hero.classList.add('is-autoplaying');
      });
      this.timer = window.setTimeout(() => {
        this.timer = 0;
        this.showStep(1, 'autoplay');
      }, this.duration);
    }

    stopAutoplay() {
      if (this.timer) window.clearTimeout(this.timer);
      if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
      this.timer = 0;
      this.progressFrame = 0;
      this.hero.classList.remove('is-autoplaying');
    }

    showStep(direction, source) {
      const next = (this.active + direction + this.slides.length) % this.slides.length;
      this.show(next, source);
    }

    show(index, source = 'manual') {
      if (this.destroyed) return;
      const next = Number(index);
      if (!Number.isInteger(next) || next < 0 || next >= this.slides.length) return;
      if (source !== 'autoplay') this.manualStopped = true;
      this.stopAutoplay();
      if (next === this.active) {
        if (source === 'keyboard') this.selectors[next]?.focus({ preventScroll: true });
        this.syncAutoplay();
        return;
      }

      const previousIndex = this.active;
      const previous = this.slides[previousIndex];
      const incoming = this.slides[next];
      this.clearLeavingTimer(incoming);
      incoming.classList.remove('is-leaving');
      previous.classList.add('is-leaving');
      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');
      incoming.classList.add('is-active');
      incoming.setAttribute('aria-hidden', 'false');

      this.selectors.forEach((selector, selectorIndex) => {
        const selected = selectorIndex === next;
        selector.classList.toggle('is-active', selected);
        selector.setAttribute('aria-selected', String(selected));
        selector.tabIndex = selected ? 0 : -1;
      });

      this.active = next;
      this.hero.dataset.activeSlide = String(next);
      this.updateCTA(next, false);
      this.prepareImage((next + 1) % this.slides.length);
      this.scheduleLeavingCleanup(previous);
      if (source === 'keyboard') this.selectors[next]?.focus({ preventScroll: true });
      this.syncAutoplay();
    }

    scheduleLeavingCleanup(slide) {
      this.clearLeavingTimer(slide);
      const delay = prefersReducedMotion() ? 0 : this.crossfade + 200;
      const timer = window.setTimeout(() => {
        this.leavingTimers.delete(slide);
        if (slide !== this.slides[this.active]) slide.classList.remove('is-leaving');
      }, delay);
      this.leavingTimers.set(slide, timer);
    }

    clearLeavingTimer(slide) {
      const timer = this.leavingTimers.get(slide);
      if (timer) window.clearTimeout(timer);
      this.leavingTimers.delete(slide);
    }

    updateCTA(index, immediate) {
      const item = this.config[index] || {};
      if (this.ctaTimer) window.clearTimeout(this.ctaTimer);
      if (this.ctaFrame) window.cancelAnimationFrame(this.ctaFrame);
      this.ctaTimer = 0;
      this.ctaFrame = 0;

      const apply = () => {
        this.ctaTimer = 0;
        if (this.destroyed || this.signal.aborted) return;
        this.ctaLabel.textContent = item.label || 'DESCOPERĂ COLECȚIA';
        this.cta.href = item.url || this.cta.getAttribute('href') || '/collections/inele-de-logodna';
        this.ctaFrame = window.requestAnimationFrame(() => {
          this.ctaFrame = 0;
          this.cta.classList.remove('is-changing');
        });
      };

      if (immediate || prefersReducedMotion()) {
        apply();
        return;
      }
      this.cta.classList.add('is-changing');
      this.ctaTimer = window.setTimeout(apply, 260);
    }

    prepareImage(index) {
      const image = this.slides[index]?.querySelector('img');
      if (!image) return;
      image.loading = 'eager';
      image.fetchPriority = index === this.active ? 'high' : 'low';
    }

    onMotionPreferenceChange() {
      if (!this.ready) return;
      if (prefersReducedMotion()) {
        this.leavingTimers.forEach((timer, slide) => {
          window.clearTimeout(timer);
          slide.classList.remove('is-leaving');
        });
        this.leavingTimers.clear();
      }
      this.syncAutoplay();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.stopAutoplay();
      if (this.ctaTimer) window.clearTimeout(this.ctaTimer);
      if (this.ctaFrame) window.cancelAnimationFrame(this.ctaFrame);
      this.leavingTimers.forEach((timer, slide) => {
        window.clearTimeout(timer);
        slide.classList.remove('is-leaving');
      });
      this.leavingTimers.clear();
      this.visibilityObserver?.disconnect();
      this.hero.classList.remove('is-manual-dragging', 'is-autoplaying');
      this.hero.style.removeProperty('--manual-drag-x');
      this.cta?.classList.remove('is-changing');
    }
  }

  class HomeController {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.revealObserver = null;
      this.consultationOverlayObserver = null;
      this.consultationFrame = 0;
      this.destroyed = false;

      prepareAnimationEnvironment();
      this.revealItems = [...root.querySelectorAll('.creveal')];
      this.initRevealObserver();

      const hero = root.querySelector('[data-corsaille-hero]');
      this.heroController = hero && !hero.hasAttribute('data-fade-morph')
        ? new HeroController(hero, this.signal)
        : null;
      this.initConsultationVisibility();

      listenForMotionPreference(() => {
        if (prefersReducedMotion()) this.revealAll();
        this.heroController?.onMotionPreferenceChange();
      }, this.signal);
    }

    initRevealObserver() {
      if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        this.revealAll();
        return;
      }
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver?.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      this.revealItems.forEach((item) => this.revealObserver.observe(item));
    }

    revealAll() {
      this.revealObserver?.disconnect();
      this.revealObserver = null;
      this.revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    initConsultationVisibility() {
      // The homepage omits the floating advisor. Avoid layout reads and global
      // overlay/scroll observers when their only visual consumer is absent.
      if (!document.querySelector('.ccv__whatsapp')) return;
      const hero = this.root.querySelector('.chero');
      if (!hero) return;
      const html = document.documentElement;
      const siteFooter = document.querySelector('.cfooter, footer[role="contentinfo"], [data-section-type="footer"]');
      const homeTop = this.root.getBoundingClientRect().top + window.scrollY;

      const update = () => {
        this.consultationFrame = 0;
        if (this.destroyed) return;
        const revealAt = homeTop + hero.offsetHeight * 0.78;
        const footerNear = siteFooter
          ? siteFooter.getBoundingClientRect().top < (window.innerHeight || 1) - 24
          : false;
        const overlayOpen = Boolean(document.querySelector('cart-drawer.active,.drawer.active,.menu-opening,dialog[open]'));
        const visible = window.scrollY >= revealAt && !footerNear && !overlayOpen;
        html.classList.toggle('corsaille-home-consult-visible', visible);
        html.classList.toggle('corsaille-home-consult-suppressed', footerNear || overlayOpen);
      };

      const schedule = () => {
        if (this.consultationFrame || this.destroyed) return;
        this.consultationFrame = window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', schedule, { passive: true, signal: this.signal });
      window.addEventListener('resize', schedule, { signal: this.signal });
      document.addEventListener('click', schedule, { signal: this.signal });
      document.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') schedule();
      }, { signal: this.signal });
      document.addEventListener('close', schedule, { capture: true, signal: this.signal });

      if ('MutationObserver' in window && document.body) {
        this.consultationOverlayObserver = new MutationObserver(schedule);
        this.consultationOverlayObserver.observe(document.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'open'],
        });
      }

      update();
      this.signal.addEventListener('abort', () => {
        if (this.consultationFrame) window.cancelAnimationFrame(this.consultationFrame);
        this.consultationFrame = 0;
        this.consultationOverlayObserver?.disconnect();
        this.consultationOverlayObserver = null;
        html.classList.remove('corsaille-home-consult-visible', 'corsaille-home-consult-suppressed');
      }, { once: true });
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.controller.abort();
      this.revealObserver?.disconnect();
      this.revealObserver = null;
      this.heroController?.destroy();
    }
  }

  const initialize = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new HomeController(root));
    });
  };

  const destroy = (scope) => {
    rootsWithin(scope).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  const lifecycleController = new AbortController();
  window.CorsailleHomeV2 = { initialize, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), {
      once: true,
      signal: lifecycleController.signal,
    });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target), {
    signal: lifecycleController.signal,
  });
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target), {
    signal: lifecycleController.signal,
  });
})();
